import { Input } from "@/components/ui/input";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";
import {
  formatDateForInput,
  parseCalendarDateFromInput,
} from "@/shared/lib/date-time";
import { DatePicker } from "@/shared/ui/DatePicker";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { PURCHASE_ORDER_STATUSES } from "@/server/platform/db/schema/purchase-orders";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useSuppliersLookupQuery } from "../../api/suppliers-queries";
import { PO_STATUS_LABELS } from "./purchaseLaoLabels";

const FILTER_ALL = "__all__" as const;

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

export type PurchaseOrdersSearch = {
  status?: string;
  supplierId?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
  poNo?: string;
  offset?: number;
  limit?: number;
};

export function PurchaseOrdersFilter() {
  const nav = useNavigate({ from: "/app/inventory/purchase-orders" });
  const search = useSearch({
    from: "/app/inventory/purchase-orders",
  }) as PurchaseOrdersSearch;

  const suppliers = useSuppliersLookupQuery();
  const [poNoValue, setPoNoValue] = useState(search.poNo ?? "");

  useEffect(() => {
    setPoNoValue(search.poNo ?? "");
  }, [search.poNo]);

  const debouncedPoNo = useDebounceCallback((val: string) => {
    setPoNoValue(val);
    update({ poNo: val.trim() ? val.trim() : undefined });
  }, 400);

  const statusOptions = [
    { value: FILTER_ALL, label: "ສະຖານະທັງໝົດ" },
    ...PURCHASE_ORDER_STATUSES.map((s) => ({
      value: s,
      label: PO_STATUS_LABELS[s],
    })),
  ];

  const supplierOptions = [
    { value: FILTER_ALL, label: "ຜູ້ສະໜອງທັງໝົດ" },
    ...(suppliers.data ?? []).map((s) => ({
      value: s.id,
      label: s.code ? `${s.name} (${s.code})` : s.name,
    })),
  ];

  const hasFilter =
    !!search.poNo?.trim() ||
    !!search.status ||
    !!search.supplierId ||
    !!search.orderDateFrom ||
    !!search.orderDateTo;

  const update = (next: Partial<PurchaseOrdersSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    nav({
      search: {
        limit: search.limit ?? 20,
        offset: 0,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
      <Input
        placeholder="ເລກ PO..."
        defaultValue={poNoValue}
        onChange={(e) => debouncedPoNo(e.target.value)}
        className="h-9 max-w-[160px]"
      />
      <SimpleSelect
        placeholder="ສະຖານະ"
        value={search.status ?? FILTER_ALL}
        onValueChange={(v) => update({ status: toParam(v ?? "") })}
        options={statusOptions}
        className="h-9 w-44"
      />
      <SimpleSelect
        placeholder="ຜູ້ສະໜອງ"
        value={search.supplierId ?? FILTER_ALL}
        onValueChange={(v) => update({ supplierId: toParam(v ?? "") })}
        options={supplierOptions}
        className="h-9 min-w-44 max-w-56"
        contentClassName="max-w-[min(100vw-2rem,20rem)]"
      />
      <DatePicker
        mode="range"
        value={{
          from: search.orderDateFrom
            ? parseCalendarDateFromInput(search.orderDateFrom)
            : undefined,
          to: search.orderDateTo
            ? parseCalendarDateFromInput(search.orderDateTo)
            : undefined,
        }}
        onChange={(range) => {
          const from = range?.from;
          const to = range?.to;
          update({
            orderDateFrom: from ? formatDateForInput(from) : undefined,
            orderDateTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ວັນທີສັ່ງ"
        className="h-9 w-fit min-w-56"
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex h-9 items-center gap-1 px-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
